/**
 * De onde sai o CAMINHO de cada arquivo que a pessoa escolheu.
 *
 * É a parte da tela que não é tela: três formas de escolher arquivo, e as três
 * entregam o caminho relativo de um jeito diferente. Separado num módulo porque
 * é lógica pura — dá para ler sem abrir o React.
 *
 * ## Por que o caminho importa tanto
 *
 * O conteúdo é HTML autocontido, e ele se refere às próprias partes por
 * caminho relativo: `<link href="css/estilo.css">`, `<img src="img/foto.png">`.
 * Se o `css/estilo.css` for gravado como `estilo.css`, a página sobe e abre
 * **sem o estilo** — sem erro, sem aviso, só feia. Preservar a pasta é o que
 * faz um protótipo de verdade funcionar.
 *
 * ## As três formas, e o que cada uma dá
 *
 *   escolher arquivos   `<input multiple>` → só `file.name`. Sem pasta, e isso
 *                       é o certo: quem escolheu três arquivos soltos quer
 *                       três arquivos na raiz
 *
 *   escolher pasta      `<input webkitdirectory>` → `file.webkitRelativePath`,
 *                       que vem com o nome da pasta escolhida na frente:
 *                       `meu-protótipo/css/estilo.css`. O primeiro segmento
 *                       **sai**, senão a página inteira ficaria enterrada num
 *                       nível a mais e todo link relativo continuaria errado
 *
 *   arrastar e soltar   `DataTransferItem.webkitGetAsEntry()`, que é a única
 *                       forma de descer numa pasta arrastada. Soltar arquivos
 *                       soltos dá caminho sem pasta; soltar uma pasta dá a
 *                       árvore inteira, e aqui também o primeiro segmento sai
 */

/** Tira o primeiro segmento: `protótipo/css/a.css` → `css/a.css`. */
const semARaiz = (caminho) => caminho.split('/').slice(1).join('/') || caminho;

/**
 * O que veio de um `<input type="file">`.
 *
 * @param {FileList|File[]} lista
 * @param {boolean} ehPasta  verdadeiro quando o input tem `webkitdirectory`
 * @returns {{path: string, file: File}[]}
 */
export function deInput(lista, ehPasta = false) {
  return Array.from(lista ?? []).map((file) => ({
    path: ehPasta && file.webkitRelativePath ? semARaiz(file.webkitRelativePath) : file.name,
    file,
  }));
}

/**
 * Desce numa entrada arrastada, recursivamente.
 *
 * `readEntries` devolve os filhos em lotes e **não avisa quantos faltam**: um
 * diretório com muitos arquivos chega em pedaços, e parar no primeiro lote
 * perderia o resto em silêncio. Daí o laço até vir vazio.
 */
async function lerEntrada(entry, prefixo, destino) {
  if (entry.isFile) {
    const file = await new Promise((ok, falhou) => entry.file(ok, falhou));
    destino.push({ path: prefixo ? `${prefixo}/${entry.name}` : entry.name, file });
    return;
  }

  if (!entry.isDirectory) return;

  const reader = entry.createReader();
  const caminhoAqui = prefixo ? `${prefixo}/${entry.name}` : entry.name;

  for (;;) {
    const lote = await new Promise((ok, falhou) => reader.readEntries(ok, falhou));
    if (lote.length === 0) break;
    for (const filho of lote) await lerEntrada(filho, caminhoAqui, destino);
  }
}

/**
 * O que veio de um arrastar-e-soltar.
 *
 * ## `webkitGetAsEntry` tem de ser chamado ANTES de qualquer `await`
 *
 * A lista de `items` do `DataTransfer` só vale durante o tratamento do evento:
 * depois do primeiro `await` ela é esvaziada pelo navegador, e as chamadas
 * seguintes devolvem `null`. Por isso as entradas são colhidas num laço
 * síncrono primeiro, e só depois percorridas.
 *
 * É o tipo de detalhe que funciona na máquina de quem escreveu, com uma pasta
 * de dois arquivos, e falha com uma de quarenta.
 *
 * @returns {Promise<{path: string, file: File}[]>}
 */
export async function deSoltar(dataTransfer) {
  const entradas = [];
  for (const item of Array.from(dataTransfer?.items ?? [])) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) entradas.push(entry);
  }

  // Navegador sem a API de entradas: sobra a lista de arquivos, sem pasta.
  if (entradas.length === 0) return deInput(dataTransfer?.files);

  const colhidos = [];
  for (const entry of entradas) {
    await lerEntrada(entry, '', colhidos);
  }

  // Uma pasta só arrastada: o nome dela não entra no caminho, pelo mesmo motivo
  // do `webkitdirectory`. Vários itens soltos ficam como vieram — aí os nomes
  // de pasta distinguem um do outro e tirá-los juntaria coisas diferentes.
  const soUmaPasta = entradas.length === 1 && entradas[0].isDirectory;
  return soUmaPasta ? colhidos.map((a) => ({ ...a, path: semARaiz(a.path) })) : colhidos;
}

export default { deInput, deSoltar };
